import {
  Bus,
  Car,
  ExternalLink,
  Footprints,
  MoveDown,
  MoveRight,
  Thermometer,
  TrainFront,
} from "lucide-react";
import { formatDuration, formatTime } from "../lib/format";
import { directionsUrl } from "../lib/deeplinks";
import { dictionaries } from "../lib/i18n";
import type { Connection, Mode, Stop, UiLang } from "../lib/types";
import { routeAlerts } from "../utils/routeAlerts";

/**
 * Strekningen mellom to stopp, vist i mellomrommet mellom dem på tidslinja.
 *
 * Planen sa hvor man skulle og hvorfor, men aldri hvordan man kom seg dit.
 * For den som lander i Bergen uten bil er det første spørsmål: går det tog,
 * må jeg leie, eller kan jeg gå? Svaret sto ingen steder — avgangene fantes,
 * men bare én gang per dag, mellom gårsdagens siste stopp og dagens første.
 *
 * Raden står *mellom* stoppene og ikke inne i dem med vilje: en reise er
 * mellomrommene like mye som stedene, og det er mellomrommene man må planlegge.
 */

const MODE_ICON: Record<Mode, typeof Car> = {
  bil: Car,
  tog: TrainFront,
  buss: Bus,
  fottur: Footprints,
};

interface Props {
  connection: Connection;
  /**
   * Endene, for koordinatene veibeskrivelsen trenger.
   *
   * Bare `lat`/`lon`, ikke et helt `Stop`: etappen fram til der ruta begynner
   * går fra et sted som ikke er med i planen, og som derfor ikke finnes som
   * `Stop` her. `directionsUrl` har aldri trengt mer enn koordinatene.
   */
  from?: { lat: number; lon: number };
  to?: { lat: number; lon: number; elevationM?: number };
  lang: UiLang;
}

export function ConnectionRow({ connection, from, to, lang }: Props) {
  const t = dictionaries[lang];
  const Icon = MODE_ICON[connection.mode] ?? Car;
  const minutes = Math.round(connection.hours * 60);
  const link = from && to ? directionsUrl(from, to, connection.mode) : null;
  /*
   * Klimasjokkvarselet trenger høyden i den andre enden. Den finnes ikke for
   * hvert sted en etappe kan gå til — startstedet man reiser fra er ikke et stopp
   * i planen — og et varsel om 900 meter opp krever at vi vet at det er 900.
   */
  const alerts = to?.elevationM != null ? routeAlerts(connection, to.elevationM, lang) : [];

  /*
   * Alternativene vises bare når det finnes et reelt valg. Én reise er ikke et
   * valg, den er bare reisen — og en overskrift som lover «andre veier» over en
   * liste med én rad, er verre enn ingen overskrift.
   */
  const journeys = connection.journeys ?? [];
  const alternatives = journeys.length > 1 ? journeys : [];

  /*
   * Avgangen man skal ta, skilt fra de man kan ta i stedet.
   *
   * Entur svarer sortert på avgangstid, så den første er den neste som går.
   * Resten er nyttige, men de er *valg* — og et valg presentert som svaret er
   * grunnen til at ingen fant toget sitt.
   */
  const [boarding, ...later] = connection.departures;

  return (
    <div className="connection">
      <div className="connection-rail" aria-hidden="true">
        <Icon size={13} />
      </div>

      <div className="connection-body">
        <div className="connection-head">
          <span className="connection-route">
            {connection.fromName} <MoveRight size={11} /> {connection.toName}
          </span>
          <span className="connection-facts">
            {t.connectionSummary(
              connection.straightKm,
              formatDuration(minutes, lang),
              t.modeName[connection.mode].toLowerCase(),
            )}
          </span>
          {connection.walkable && <span className="connection-flag">{t.connectionWalkable}</span>}
          {connection.trailhead && (
            <span className="connection-flag">{t.connectionTrailhead}</span>
          )}
          {/*
            At det også går kollektivt her.

            Meldt fra bruk: «for noen ruter finnes det faktisk toget eller
            buss, men den viser vi ikke». Den som velger bil fikk aldri vite at
            et tog følger den samme dalen — planen viste bare en varighet og en
            lenke ut i kartet. Merkelappen er en opplysning, ikke et forslag om
            å bytte: den sier at valget finnes på akkurat denne strekningen.
            Uten avgangstider, fordi de ville kostet et oppslag hos Entur for
            en reise brukeren ikke har sagt at de skal ta.
          */}
          {connection.alsoTransit && (
            <span className="connection-flag connection-flag--also">
              {t.connectionAlsoBy(t.modeName[connection.alsoTransit].toLowerCase())}
            </span>
          )}
        </div>

        {/*
          Avgangen man faktisk skal ta, som en egen blokk.
          
          Meldt fra bruk: «hvis jeg velger tog, er det vanskelig å se hvilket
          tog jeg må ta fra forrige stopp». Avgangene *fantes* — de sto som en
          rad små spann i 11 px monospace, visuelt likt alt annet på linja, uten
          at noe pekte på hvilken av dem som var svaret. Den som skal ta et tog
          trenger ett svar, ikke en liste å tolke.
          
          Den første avgangen er den blokka svarer med, og den er den riktige:
          Entur sorterer på avgangstid, så den første er den neste som går.
          Stasjonsnavnet kommer fra avgangen og ikke fra stoppet — de er ikke
          alltid det samme, og «fra Myrdal stasjon» er svaret på «fra forrige
          stopp» når stoppet heter Flåm.
        */}
        {boarding && (
          <div className="connection-board">
            <span className="connection-board-line">{boarding.line}</span>
            <span className="connection-board-time">
              <b>{formatTime(boarding.aimedDeparture, lang)}</b>
              <small>{t.connectionFrom(boarding.from)}</small>
            </span>
            <MoveRight className="connection-board-arrow" size={13} aria-hidden="true" />
            <span className="connection-board-time">
              <b>{formatTime(boarding.aimedArrival, lang)}</b>
              <small>{boarding.to}</small>
            </span>
            {boarding.durationMinutes > 0 && (
              <span className="connection-board-dur">
                {formatDuration(boarding.durationMinutes, lang)}
              </span>
            )}
          </div>
        )}

        {/*
          De senere avgangene, som en liste å skumme. Den første står over, så
          denne begynner på den andre — ellers gjentar den svaret og gjør det
          uklart igjen hvilken av dem som var det.
        */}
        {later.length > 0 && (
          <div className="connection-departures">
            <span className="connection-departures-label">{t.connectionLater}</span>
            {later.map((departure, index) => (
              <span className="connection-departure" key={`${departure.line}-${index}`}>
                <strong>{departure.line}</strong> {formatTime(departure.aimedDeparture, lang)}
                {" → "}
                {formatTime(departure.aimedArrival, lang)}
              </span>
            ))}
          </div>
        )}

        {/*
          De andre måtene å reise strekningen på.

          Entur svarer med flere forslag på det samme spørsmålet, og appen ba
          lenge om nøyaktig ett. På Vestlandet er det sjelden riktig: en ferje
          som går sjeldnere, men kutter to timer, så ut til ikke å finnes.
          Alternativene kostet ingen ekstra forespørsel — de lå i svaret vi
          allerede fikk.
        */}
        {alternatives.length > 0 && (
          <div className="journeys">
            <span className="journeys-label">{t.journeyAlternatives}</span>
            {alternatives.map((journey, index) => (
              <div className="journey" key={`${journey.modes.join("-")}-${journey.durationMinutes}`}>
                <span className="journey-time">
                  {formatDuration(journey.durationMinutes, lang)}
                </span>
                {index === 0 && <span className="journey-best">{t.journeyFastest}</span>}
                <span className="journey-legs">
                  {journey.legs.map((leg, at) => (
                    <span className="journey-leg" key={`${leg.line}-${at}`}>
                      {at > 0 && <MoveRight size={9} />}
                      <strong>{leg.line}</strong>
                      {" "}
                      {formatTime(leg.aimedDeparture, lang)}
                    </span>
                  ))}
                </span>
                <span className="journey-transfers">
                  {journey.transfers === 0
                    ? t.journeyDirect
                    : t.journeyTransfers(journey.transfers)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/*
          Klimasjokk og lange tunneler.

          Står her og ikke på stoppet fordi det er noe som skjer underveis:
          man stiger tusen meter mellom Skjolden og Sognefjellshytta, og
          kulden møter en på veien, ikke ved ankomst. Se utils/routeAlerts.ts.
        */}
        {alerts.map((alert) => (
          <div className={`route-alert route-alert--${alert.kind}`} key={alert.title}>
            {alert.kind === "climate" ? <Thermometer size={13} /> : <MoveDown size={13} />}
            <span>
              <strong>{alert.title}</strong> {alert.detail}
            </span>
          </div>
        ))}

        {link && (
          <a className="connection-link no-print" href={link} target="_blank" rel="noreferrer">
            {t.connectionDirections}
            <ExternalLink size={11} />
          </a>
        )}
      </div>
    </div>
  );
}
